import React from 'react';
import { styled } from '@mui/material/styles';
import { radius } from '../theme/tokens.radius';

interface UIKitInputGroupProps {
  children: React.ReactNode;
  /** Si es true, el grupo ocupa todo el ancho disponible */
  fullWidth?: boolean;
  className?: string;
}

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  display: 'inline-flex',
  alignItems: 'stretch',
  ...(fullWidth && { width: '100%' }),
}));

// item's styles se aplican a hijos clonados arbitrarios (ver nota en
// ButtonGroup.tsx) - un styled() wrapper agregaría un nodo de DOM extra por
// hijo y rompería el layout de flex-stretch.
const itemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
};

const UIKitInputGroup: React.FC<UIKitInputGroupProps> = ({
  children,
  fullWidth = true,
  className,
}) => {
  // Los hijos son componentes UIKit heterogéneos (Input, DropDown, etc.) - lo único
  // que este grupo necesita tocar de cada uno es className/style, comunes a todos.
  const childArray = React.Children.toArray(children) as React.ReactElement<{
    className?: string;
    style?: React.CSSProperties;
  }>[];

  return (
    <Wrapper fullWidth={fullWidth} className={className}>
      {childArray.map((child, index) => {
        if (!React.isValidElement(child)) {
          return child;
        }

        const isFirst = index === 0;
        const isLast = index === childArray.length - 1;

        const stylePatch: React.CSSProperties = {};

        if (childArray.length === 1) {
          // único hijo: radios normales
          stylePatch.borderRadius = radius.xs;
        } else if (isFirst) {
          stylePatch.borderTopLeftRadius = radius.xs;
          stylePatch.borderBottomLeftRadius = radius.xs;
          stylePatch.borderTopRightRadius = 0;
          stylePatch.borderBottomRightRadius = 0;
        } else if (isLast) {
          stylePatch.borderTopLeftRadius = 0;
          stylePatch.borderBottomLeftRadius = 0;
          stylePatch.borderTopRightRadius = radius.full;
          stylePatch.borderBottomRightRadius = radius.full;
        } else {
          // hijos del medio (si alguna vez hay más de 2)
          stylePatch.borderRadius = 0;
        }

        return React.cloneElement(child, {
          className: child.props.className,
          style: {
            ...itemStyle,
            ...child.props.style,
            ...stylePatch, // 👈 el grupo fuerza solo el borderRadius
          },
        });
      })}
    </Wrapper>
  );
};

export default UIKitInputGroup;
